import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Save, ShieldAlert, Info } from 'lucide-react';
import { getSettings, updateSettings } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import type { RiskConfig, FacetWeights } from '../../types';
import { DEFAULT_RISK_CONFIG } from '../../types';

// ── Threshold options ─────────────────────────────────────────────────────

const THRESHOLD_OPTIONS: { value: number; label: string; description: string }[] = [
  { value: 2.0, label: 'Very Sensitive', description: 'Flag early-stage concerns' },
  { value: 2.5, label: 'Sensitive', description: 'Catch issues before they grow' },
  { value: 3.0, label: 'Balanced', description: 'Flag noticeable risk (default)' },
  { value: 3.5, label: 'Conservative', description: 'Flag significant risk only' },
  { value: 4.0, label: 'High Threshold', description: 'Reserve for serious situations' },
  { value: 4.5, label: 'Critical Only', description: 'Near-emergency situations only' },
];

// ── Facet metadata ────────────────────────────────────────────────────────

const FACETS: { key: keyof FacetWeights; label: string; description: string }[] = [
  { key: 'communication', label: 'Communication', description: 'Team alignment, update health, knowledge sharing' },
  { key: 'schedule', label: 'Schedule', description: 'Milestone tracking and velocity consistency' },
  { key: 'definition', label: 'Definition', description: 'Requirements clarity and PRD completeness' },
  { key: 'scope', label: 'Scope', description: 'Feature creep detection and requirement volatility' },
  { key: 'dependency', label: 'Dependency', description: 'External blockers and cross-team wait states' },
  { key: 'deadline', label: 'Deadline', description: 'Overall delivery confidence and committed dates' },
];

const WEIGHT_OPTIONS: { value: number; label: string }[] = [
  { value: 0.5, label: 'Low' },
  { value: 1.0, label: 'Normal' },
  { value: 2.0, label: 'High Priority' },
];

const TICKET_TYPES = [
  { value: 'feature', label: 'Feature' },
  { value: 'enhancement', label: 'Enhancement' },
  { value: 'bug_fix', label: 'Bug Fix' },
  { value: 'task', label: 'Task' },
];

// ── Preview calculation ───────────────────────────────────────────────────

function computePreviewScore(weights: FacetWeights): number {
  // Sample scores for a mid-risk project
  const sample = { communication: 3, schedule: 4, definition: 2, scope: 3, dependency: 2, deadline: 4 };
  let weightedSum = 0;
  let totalWeight = 0;
  for (const facet of FACETS) {
    const w = weights[facet.key];
    weightedSum += sample[facet.key] * w;
    totalWeight += w;
  }
  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 10) / 10 : 3.0;
}

// ── Component ─────────────────────────────────────────────────────────────

export function RiskConfiguration() {
  const qc = useQueryClient();
  const { data: settingsData, isLoading } = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
  });

  const [config, setConfig] = useState<RiskConfig>(DEFAULT_RISK_CONFIG);
  const [initialized, setInitialized] = useState(false);

  if (settingsData && !initialized) {
    setConfig({ ...DEFAULT_RISK_CONFIG, ...(settingsData.riskConfig ?? {}) });
    setInitialized(true);
  }

  const mutation = useMutation({
    mutationFn: () => updateSettings({ riskConfig: config }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });

  const setWeight = (key: keyof FacetWeights, value: number) => {
    setConfig((prev) => ({
      ...prev,
      facetWeights: { ...prev.facetWeights, [key]: value },
    }));
  };

  const toggleTicketType = (type: string) => {
    setConfig((prev) => {
      const current = prev.includedTicketTypes;
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, includedTicketTypes: next };
    });
  };

  const previewScore = computePreviewScore(config.facetWeights);
  const previewStatus = previewScore >= config.alertThreshold ? 'At Risk' : 'On Track';
  const previewStatusColor = previewScore >= config.alertThreshold
    ? 'text-red-400'
    : 'text-green-400';

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton h-16 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8">

      {/* Section 1 — Alert Threshold */}
      <section className="space-y-4">
        <div>
          <h3 className="text-title text-gray-900 dark:text-white flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4 text-primary" />
            Alert Threshold
          </h3>
          <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
            At what risk level should a project be flagged as At Risk?
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {THRESHOLD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setConfig((p) => ({ ...p, alertThreshold: opt.value }))}
              className={`text-left px-3 py-2.5 rounded-xl border text-meta transition-colors ${
                config.alertThreshold === opt.value
                  ? 'bg-primary/10 dark:bg-primary/20 border-primary/40 text-primary'
                  : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-primary/30'
              }`}
            >
              <div className="font-medium">{opt.label}</div>
              <div className="text-micro opacity-70 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Section 2 — Facet Weights */}
      <section className="space-y-4">
        <div>
          <h3 className="text-title text-gray-900 dark:text-white">
            Facet Weights
          </h3>
          <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
            Which risk dimensions matter most to your team?
          </p>
        </div>
        <div className="space-y-4">
          {FACETS.map((facet) => {
            const currentWeight = config.facetWeights[facet.key];
            return (
              <div
                key={facet.key}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-body font-medium text-gray-900 dark:text-white">
                    {facet.label}
                  </p>
                  <p className="text-micro text-gray-500 dark:text-gray-500 truncate">
                    {facet.description}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {WEIGHT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setWeight(facet.key, opt.value)}
                      className={`px-2.5 py-1 rounded-lg text-micro font-medium border transition-colors ${
                        currentWeight === opt.value
                          ? 'bg-primary text-white border-primary'
                          : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-primary/30'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Preview */}
        <div className="p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] flex items-center gap-2 text-meta text-gray-500 dark:text-gray-400">
          <Info className="h-4 w-4 flex-shrink-0" />
          <span>
            With these weights, a mid-risk project would score{' '}
            <span className="font-semibold text-gray-900 dark:text-white">{previewScore.toFixed(1)}</span>
            {' '}—{' '}
            <span className={`font-semibold ${previewStatusColor}`}>{previewStatus}</span>
          </span>
        </div>
      </section>

      {/* Section 3 — Evaluation Sensitivity */}
      <section className="space-y-4">
        <div>
          <h3 className="text-title text-gray-900 dark:text-white">
            Evaluation Sensitivity
          </h3>
          <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
            Fine-tune how the AI evaluates your projects.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
              Minimum tickets for a score
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={0}
                max={20}
                value={config.minTicketsForScore}
                onChange={(e) =>
                  setConfig((p) => ({
                    ...p,
                    minTicketsForScore: Math.max(0, Math.min(20, parseInt(e.target.value) || 0)),
                  }))
                }
                className="w-20 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta text-center focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
              />
              <span className="text-meta text-gray-500 dark:text-gray-400">
                Projects with fewer tickets show a low-confidence badge
              </span>
            </div>
          </div>

          <div>
            <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
              Stale ticket threshold (days)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={30}
                value={config.staleTicketDays}
                onChange={(e) =>
                  setConfig((p) => ({
                    ...p,
                    staleTicketDays: Math.max(1, Math.min(30, parseInt(e.target.value) || 5)),
                  }))
                }
                className="w-20 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta text-center focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
              />
              <span className="text-meta text-gray-500 dark:text-gray-400">
                Tickets with no update after this many days are flagged
              </span>
            </div>
          </div>

          <div>
            <label className="block text-meta text-gray-500 dark:text-gray-400 mb-2">
              Ticket types included in risk scoring
            </label>
            <div className="flex flex-wrap gap-2">
              {TICKET_TYPES.map((t) => {
                const checked = config.includedTicketTypes.includes(t.value);
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => toggleTicketType(t.value)}
                    className={`px-3 py-1.5 rounded-lg text-micro font-medium border transition-colors ${
                      checked
                        ? 'bg-primary/10 dark:bg-primary/20 border-primary/40 text-primary'
                        : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-primary/30'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-meta text-gray-500 dark:text-gray-500 mt-1.5">
              Ideas and invalid tickets are always excluded.
            </p>
          </div>
        </div>
      </section>

      {/* Save */}
      {mutation.isError && (
        <p className="text-meta text-red-400 bg-red-900/20 border border-red-900/30 rounded-lg p-3">
          {(mutation.error as Error).message}
        </p>
      )}
      {mutation.isSuccess && (
        <p className="text-meta text-green-400 bg-green-900/20 border border-green-900/30 rounded-lg p-3">
          Risk configuration saved.
        </p>
      )}

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <Save className="h-4 w-4" />
        {mutation.isPending ? 'Saving...' : 'Save Risk Configuration'}
      </motion.button>
    </div>
  );
}
